import React, { useState, useEffect } from 'react';
import { BarChart2, Loader2, TrendingUp, Users, Cpu, DollarSign, Eye, ExternalLink, Check, AlertCircle, Shield, X, Info, HardDrive } from 'lucide-react';
import { API } from '../../App';

function StatCard({ icon: Icon, label, value, sub, onClick }) {
  const clickable = typeof onClick === 'function';
  return (
    <div
      className={`p-4 bg-[#0F0F0F] border border-[#2A2A2A] ${clickable ? 'cursor-pointer hover:border-[#FF4D00]/60 transition-colors' : ''}`}
      onClick={onClick}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter') onClick(); } : undefined}
    >
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4 text-[#FF4D00]" />
        <span className="text-xs text-[#A0A0A0] font-mono">{label}</span>
      </div>
      <p className="text-xl font-bold font-['Barlow_Condensed']">{value}</p>
      {sub && <p className="text-xs text-[#A0A0A0]">{sub}</p>}
    </div>
  );
}

function MiniBar({ value, max }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div className="h-2 bg-[#2A2A2A] flex-1 max-w-[120px]">
      <div className="h-full bg-[#FF4D00]" style={{ width: `${pct}%` }} />
    </div>
  );
}

function IOLoggingCard({ wsId }) {
  const [obs, setObs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    if (!wsId) return;
    API.get(`/api/workspaces/${wsId}/observability`)
      .then(r => setObs(r.data))
      .catch(() => setObs(null))
      .finally(() => setLoading(false));
  }, [wsId]);

  const toggle = async () => {
    if (!obs) return;
    const newVal = !obs.or_io_logging?.enabled;
    if (newVal && !window.confirm(
      'Before enabling, you must turn on Input & Output Logging in your OpenRouter workspace settings.\n\n' +
      'Have you enabled it on OpenRouter?'
    )) return;
    setToggling(true);
    try {
      const res = await API.put(`/api/workspaces/${wsId}/observability`, { enabled: newVal });
      setObs(prev => ({ ...prev, or_io_logging: res.data.or_io_logging }));
    } catch { /* ignore */ }
    setToggling(false);
  };

  if (loading) return null;
  if (!obs) return null;

  const enabled = obs.or_io_logging?.enabled;
  const hasKey = obs.has_or_management_key;

  return (
    <div className="p-4 bg-[#0F0F0F] border border-[#2A2A2A]">
      <div className="flex items-center gap-2 mb-3">
        <Eye className="w-4 h-4 text-[#00D4FF]" />
        <h3 className="text-sm font-bold font-['Barlow_Condensed'] uppercase tracking-wider">OpenRouter I/O Logging</h3>
        <span className={`text-[9px] font-mono px-2 py-0.5 uppercase tracking-wider border ${
          enabled
            ? 'bg-[#22C55E]/15 text-[#22C55E] border-[#22C55E]/30'
            : 'bg-[#2A2A2A] text-[#A0A0A0] border-[#2A2A2A]'
        }`}>
          {!hasKey ? 'Not Available' : enabled ? 'Enabled' : 'Disabled'}
        </span>
        <span className="text-[9px] font-mono px-2 py-0.5 bg-[#F2A900]/15 text-[#F2A900] border border-[#F2A900]/30 uppercase">Beta</span>
      </div>

      <p className="text-xs text-[#A0A0A0] mb-4">
        OpenRouter can privately store the full prompt and completion content of every fray run through this workspace.
        Stored in OpenRouter's isolated, encrypted storage (AES-256). Only your OpenRouter org admins can view the data.
        Use it for compliance review, security investigations, prompt optimization, and debugging.
      </p>

      {!hasKey ? (
        <div className="p-3 bg-[#F2A900]/10 border border-[#F2A900]/30 mb-3">
          <p className="text-xs text-[#F2A900]">
            <AlertCircle className="w-3 h-3 inline mr-1 -mt-0.5" />
            No OpenRouter management key configured. Set up a BYOK key in Providers to enable I/O logging.
          </p>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-3 mb-4">
            <button
              onClick={toggle}
              disabled={toggling}
              className={`px-4 py-2 min-h-[44px] text-xs font-medium flex items-center gap-1.5 disabled:opacity-50 ${
                enabled
                  ? 'bg-[#2A2A2A] text-[#F5F1E8]'
                  : 'bg-[#00D4FF] text-[#0A0A0A]'
              }`}
            >
              {toggling ? <Loader2 className="w-3 h-3 animate-spin" /> : <Shield className="w-3 h-3" />}
              {enabled ? 'Disable I/O Logging' : 'Enable I/O Logging'}
            </button>

            <a
              href={obs.or_settings_url}
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-2 min-h-[44px] text-xs font-mono bg-[#1E1E1E] border border-[#2A2A2A] text-[#A0A0A0] hover:text-[#F5F1E8] flex items-center gap-1.5"
            >
              OpenRouter Settings <ExternalLink className="w-3 h-3" />
            </a>
          </div>

          {enabled && (
            <div className="p-3 bg-[#22C55E]/10 border border-[#22C55E]/30 mb-3">
              <p className="text-xs text-[#22C55E]">
                <Check className="w-3 h-3 inline mr-1 -mt-0.5" />
                I/O Logging confirmed{obs.or_io_logging?.confirmed_by ? ` by ${obs.or_io_logging.confirmed_by}` : ''}{obs.or_io_logging?.confirmed_at ? ` on ${new Date(obs.or_io_logging.confirmed_at).toLocaleDateString()}` : ''}.
              </p>
              <a
                href={obs.or_logs_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 mt-2 text-xs font-mono text-[#00D4FF] hover:underline"
              >
                View logs on OpenRouter <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          )}
        </>
      )}

      <div className="p-3 bg-[#0A0A0A] border border-[#2A2A2A]">
        <p className="text-[10px] text-[#A0A0A0]">
          <strong className="text-[#F5F1E8]">Privacy:</strong> Data stored in OpenRouter's isolated Google Cloud Storage, not on Fray servers.
          Retained min. 3 months. Never used for training. Admin-only access on your OR account.
        </p>
        <p className="text-[10px] text-[#F2A900] mt-1">
          <AlertCircle className="w-3 h-3 inline mr-1 -mt-0.5" />
          Beta feature. EU-routed requests are not logged. Only generations made after enabling will have stored content.
        </p>
      </div>
    </div>
  );
}

function FrayDrilldownModal({ wsId, days, userFilter, onClose }) {
  const [frays, setFrays] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!wsId) return;
    const qs = [`days=${days}`];
    if (userFilter?.user_id) qs.push(`user_id=${encodeURIComponent(userFilter.user_id)}`);
    API.get(`/api/workspaces/${wsId}/observatory/frays?${qs.join('&')}`)
      .then(r => setFrays(r.data?.frays || []))
      .catch(() => setFrays([]))
      .finally(() => setLoading(false));
  }, [wsId, days, userFilter]);

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-[#0A0A0A] border border-[#2A2A2A] w-full max-w-4xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-[#2A2A2A]">
          <h3 className="text-sm font-bold font-['Barlow_Condensed'] uppercase tracking-wider">
            Frays — Last {days}d
            {userFilter ? ` — ${userFilter.name || userFilter.email}` : ''}
          </h3>
          <button onClick={onClose} className="text-[#A0A0A0] hover:text-[#F5F1E8]">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center gap-2 text-[#A0A0A0] p-8">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading fray list...
            </div>
          ) : (frays || []).length === 0 ? (
            <div className="text-center p-8 text-[#A0A0A0] text-sm">No frays in this window</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-[#0F0F0F] sticky top-0">
                <tr className="text-[#A0A0A0] font-mono uppercase tracking-wider">
                  <th className="text-left p-2">Created</th>
                  <th className="text-left p-2">User</th>
                  <th className="text-left p-2">Scenario</th>
                  <th className="text-left p-2">Status</th>
                  <th className="text-right p-2">Voices</th>
                  <th className="text-right p-2">Tokens</th>
                </tr>
              </thead>
              <tbody>
                {frays.map((f) => (
                  <tr key={f.fray_id} className="border-t border-[#2A2A2A] hover:bg-[#0F0F0F]">
                    <td className="p-2 font-mono text-[#A0A0A0] whitespace-nowrap">
                      {f.created_at ? new Date(f.created_at).toLocaleString() : '—'}
                    </td>
                    <td className="p-2 truncate max-w-[160px]">{f.user_name || f.user_email || '—'}</td>
                    <td className="p-2">
                      <a
                        href={`/fray/${f.fray_id}/results`}
                        className="text-[#00D4FF] hover:underline"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {f.template_name || f.scenario_excerpt || f.fray_id.slice(0, 8)}
                      </a>
                    </td>
                    <td className="p-2 font-mono">{f.status || '—'}</td>
                    <td className="p-2 text-right">{f.voice_count}</td>
                    <td className="p-2 text-right">{(f.total_tokens || 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function _fmtBytes(bytes) {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let n = bytes;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n >= 10 ? 0 : 1)} ${units[i]}`;
}

// #716: Storage Used card on the Observatory dashboard
function StorageCard({ wsId }) {
  const [storage, setStorage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!wsId) return;
    API.get(`/api/workspaces/${wsId}/storage`)
      .then(r => setStorage(r.data))
      .catch(() => setStorage(null))
      .finally(() => setLoading(false));
  }, [wsId]);

  if (loading || !storage) {
    return (
      <div className="p-4 bg-[#0F0F0F] border border-[#2A2A2A]">
        <div className="flex items-center gap-2 mb-1">
          <HardDrive className="w-4 h-4 text-[#FF4D00]" />
          <span className="text-xs text-[#A0A0A0] font-mono">Storage</span>
        </div>
        <p className="text-xl font-bold font-['Barlow_Condensed']">
          {loading ? '—' : 'unavailable'}
        </p>
      </div>
    );
  }

  const statusColor = {
    ok: '#22C55E',
    warning: '#F2A900',
    over_cap: '#EF4444',
  }[storage.status] || '#A0A0A0';

  return (
    <div
      className="p-4 bg-[#0F0F0F] border border-[#2A2A2A]"
      title={`${storage.tier} tier — cap ${_fmtBytes(storage.cap_bytes)}`}
    >
      <div className="flex items-center gap-2 mb-1">
        <HardDrive className="w-4 h-4" style={{ color: statusColor }} />
        <span className="text-xs text-[#A0A0A0] font-mono">Storage Used</span>
      </div>
      <p className="text-xl font-bold font-['Barlow_Condensed']">
        {_fmtBytes(storage.bytes_used)}
        <span className="text-xs text-[#A0A0A0] ml-2 font-mono">
          {storage.cap_pct.toFixed(1)}% of {_fmtBytes(storage.cap_bytes)}
        </span>
      </p>
      {/* Thin usage bar */}
      <div className="h-1 bg-[#1A1A1A] mt-2">
        <div
          className="h-full"
          style={{
            width: `${Math.min(storage.cap_pct, 100)}%`,
            backgroundColor: statusColor,
          }}
        />
      </div>
      <p className="text-[10px] text-[#A0A0A0] mt-1 font-mono">
        {storage.file_count} files · {storage.tier} tier
      </p>
    </div>
  );
}

export default function ObservatorySection({ wsId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [drilldown, setDrilldown] = useState(null); // null | {} (all) | {user_id, name, email}

  const load = () => {
    if (!wsId) return;
    setLoading(true);
    API.get(`/api/workspaces/${wsId}/observatory?days=${days}`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };
  useEffect(load, [wsId, days]);

  if (loading) return <div className="flex items-center gap-2 text-[#A0A0A0] py-8"><Loader2 className="w-4 h-4 animate-spin" /> Loading analytics...</div>;
  if (!data) return <div className="text-center py-8 text-[#A0A0A0]"><BarChart2 className="w-8 h-8 mx-auto mb-2 opacity-50" /><p className="text-sm">No analytics data available</p></div>;

  const maxUserCost = Math.max(...(data.per_user || []).map(u => parseFloat(u.cost) || 0), 1);

  return (
    <div className="space-y-4">
      {/* OpenRouter I/O Logging (#564) */}
      <IOLoggingCard wsId={wsId} />

      <div className="flex gap-2">
        {[7, 30, 90].map(d => (
          <button key={d} onClick={() => setDays(d)}
            className={`px-3 py-1.5 text-xs font-mono min-h-[44px] ${days === d ? 'bg-[#FF4D00] text-black' : 'bg-[#0F0F0F] border border-[#2A2A2A] text-[#A0A0A0]'}`}>
            {d}d
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          icon={TrendingUp}
          label="Frays Run"
          value={data.frays_run || 0}
          onClick={(data.frays_run || 0) > 0 ? () => setDrilldown({}) : undefined}
        />
        <StatCard icon={Users} label="Active Users" value={data.active_users || 0} />
        <StatCard icon={Cpu} label="Total Tokens" value={(data.total_tokens || 0).toLocaleString()} />
        <StatCard
          icon={DollarSign}
          label="Total Cost"
          value={`$${(data.total_cost || 0).toFixed(2)}`}
          sub={data.cost_unavailable_reason === 'upstream_issue_567' ? 'backfill pending (#717)' : undefined}
        />
      </div>

      {/* #716: Storage usage card — loads from separate endpoint so Observatory
          can render even if storage data is unavailable */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <StorageCard wsId={wsId} />
      </div>

      {data.cost_unavailable_reason === 'upstream_issue_567' && (
        <div className="p-3 bg-[#F2A900]/10 border border-[#F2A900]/30 flex items-start gap-2">
          <Info className="w-3.5 h-3.5 text-[#F2A900] flex-shrink-0 mt-0.5" />
          <p className="text-xs text-[#F2A900]">
            <strong>Cost data pending.</strong> Token counts are real, but streaming-voice cost capture
            from OpenRouter is waiting on the generation-endpoint backfill (#717). Costs appear here
            once that lands; existing frays are retroactively recovered.
          </p>
        </div>
      )}

      {data.model_distribution?.length > 0 && (
        <div className="p-4 bg-[#0F0F0F] border border-[#2A2A2A]">
          <h3 className="text-sm font-bold mb-3">Model Distribution</h3>
          <div className="space-y-2">
            {data.model_distribution.map((m, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-xs font-mono text-[#A0A0A0] w-40 truncate">{m.model || 'unknown'}</span>
                <MiniBar value={m.count} max={data.model_distribution[0]?.count || 1} />
                <span className="text-xs text-[#A0A0A0] w-8 text-right">{m.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.daily_usage?.length > 0 && (() => {
        const maxFrays = Math.max(...data.daily_usage.map(x => x.frays || 0), 1);
        return (
          <div className="p-4 bg-[#0F0F0F] border border-[#2A2A2A]">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold">Daily Usage</h3>
              <span className="text-[10px] font-mono text-[#A0A0A0]">
                peak: {maxFrays} fray{maxFrays === 1 ? '' : 's'}/day
              </span>
            </div>
            {/* #794/#795: bars + y-axis ticks share the 96px chart area.
                Keep bars in a flex row so widths are uniform; ticks overlay
                so labels don't steal bar width. */}
            <div className="relative h-24">
              <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
                {[1, 0.5, 0].map((t, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="text-[9px] font-mono text-[#A0A0A0] w-6 text-right">
                      {Math.round(maxFrays * t)}
                    </span>
                    <div className="flex-1 border-t border-[#2A2A2A]/60" />
                  </div>
                ))}
              </div>
              <div className="absolute inset-0 pl-8 flex items-end gap-[2px] overflow-x-auto">
                {data.daily_usage.map((d, i) => {
                  const h = Math.max(((d.frays || 0) / maxFrays) * 100, 2);
                  return (
                    <div
                      key={i}
                      className="bg-[#FF4D00]/80 hover:bg-[#FF4D00] flex-shrink-0"
                      style={{
                        width: Math.max(100 / data.daily_usage.length, 4) + '%',
                        height: `${h}%`,
                      }}
                      title={`${d.date}: ${d.frays} frays, ${(d.tokens || 0).toLocaleString()} tokens, $${parseFloat(d.cost || 0).toFixed(2)}`}
                    />
                  );
                })}
              </div>
            </div>
            <div className="flex justify-between mt-1 pl-8">
              <span className="text-[10px] text-[#A0A0A0]">{data.daily_usage[0]?.date}</span>
              <span className="text-[10px] text-[#A0A0A0]">{data.daily_usage[data.daily_usage.length - 1]?.date}</span>
            </div>
          </div>
        );
      })()}

      {data.per_user?.length > 0 && (
        <div className="p-4 bg-[#0F0F0F] border border-[#2A2A2A]">
          <h3 className="text-sm font-bold mb-3">Per-User Breakdown</h3>
          <div className="space-y-2">
            {data.per_user.map((u, i) => (
              <button
                key={i}
                onClick={() => setDrilldown({ user_id: u.user_id, name: u.name, email: u.email })}
                className="w-full flex items-center gap-3 text-left hover:bg-[#1A1A1A] p-1 -m-1 rounded-none transition-colors"
              >
                <span className="text-xs w-32 truncate">{u.name || u.email}</span>
                <MiniBar value={parseFloat(u.cost) || 0} max={maxUserCost} />
                <span className="text-xs text-[#A0A0A0] w-16 text-right">{u.frays} frays</span>
                <span className="text-xs text-[#FF4D00] w-16 text-right">${parseFloat(u.cost || 0).toFixed(2)}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {drilldown !== null && (
        <FrayDrilldownModal
          wsId={wsId}
          days={days}
          userFilter={drilldown?.user_id ? drilldown : null}
          onClose={() => setDrilldown(null)}
        />
      )}
    </div>
  );
}
